import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  readonly width?: number | string;
  readonly height?: number | string;
  readonly rounded?: 'sm' | 'md' | 'lg' | 'full';
}

const RADIUS = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
} as const;

/**
 * Placeholder bone — shimmer animation respects `prefers-reduced-motion`
 * (degrades to a flat tinted block).
 */
export function Skeleton({
  width,
  height = 16,
  rounded = 'md',
  className,
  style,
  ...rest
}: SkeletonProps) {
  const { t } = useI18n();
  return (
    <div
      role="status"
      aria-label={t('common:status.loading', { defaultValue: 'Loading' })}
      className={cn(
        'bg-gradient-to-r from-surface-2 via-hairline to-surface-2 bg-[length:200%_100%]',
        'motion-safe:animate-bravemove-shimmer',
        RADIUS[rounded],
        className,
      )}
      style={{ width, height, ...style }}
      {...rest}
    />
  );
}
